"use client";

import type { UseFormReturn } from "react-hook-form";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import type { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SignatureSelector } from "../../signatures/SignatureSelector";

interface SignatureOptionsSectionProps {
  form: UseFormReturn<ComposeFormValues>;
  onOpenChange: (open: boolean) => void;
  onSignatureContent: (name: string, value: string) => void;
  open: boolean;
  selectedSignature?: {
    content?: string | null;
    name?: string | null;
  };
  watchedFromAccount: string;
}

export function SignatureOptionsSection({
  form,
  onOpenChange,
  onSignatureContent,
  open,
  selectedSignature,
  watchedFromAccount,
}: SignatureOptionsSectionProps) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="rounded-md border border-border/70 p-3">
        <CollapsibleTrigger className="flex w-full items-center justify-between text-left">
          <div className="min-w-0">
            <p className="text-sm font-medium">Signature options</p>
            {selectedSignature ? (
              <p className="text-xs text-green-700">
                Signature set: <span className="font-medium">{selectedSignature.name}</span>
              </p>
            ) : (
              <p className="text-xs text-amber-700">No signature selected for this message.</p>
            )}
            {selectedSignature?.content && (
              <div className="mt-2 max-h-12 overflow-hidden rounded border border-border/60 bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                <div
                  className="line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: selectedSignature.content }}
                />
              </div>
            )}
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-3">
          <FormField
            control={form.control}
            name="signature"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Signature</FormLabel>
                <FormControl>
                  <SignatureSelector
                    connectionId={watchedFromAccount}
                    value={field.value}
                    onValueChange={field.onChange}
                    expandInline={true}
                    onSignatureContent={(content) => {
                      if (content) {
                        onSignatureContent("signature", content);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
